import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { Platform } from 'react-native';

const isWeb = Platform.OS === 'web';

// Production API URL - hardcoded for native pilot build reliability
const PRODUCTION_API_URL = 'https://api.obzidn.com/api';
export const API_BASE_URL = Constants.expoConfig?.extra?.apiBaseUrl || PRODUCTION_API_URL;
console.log('[Obzidn Rider] API Base URL:', API_BASE_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth error callback — set by AuthContext to trigger logout on 401
let _onAuthError = null;
export const setAuthErrorHandler = (handler) => {
  _onAuthError = handler;
};

// Cross-platform storage utilities
// On web, use localStorage directly as primary (more reliable than AsyncStorage shim)
export const tokenStorage = {
  async setToken(token) {
    try {
      if (isWeb && typeof localStorage !== 'undefined') {
        localStorage.setItem('authToken', token);
      }
      await AsyncStorage.setItem('authToken', token);
      console.log('[AUTH] Token stored, length:', token?.length);
    } catch (error) {
      console.error('[AUTH] Error storing token:', error);
    }
  },

  async getToken() {
    try {
      // On web, try localStorage first (more reliable)
      if (isWeb && typeof localStorage !== 'undefined') {
        const webToken = localStorage.getItem('authToken');
        if (webToken) return webToken;
      }
      return await AsyncStorage.getItem('authToken');
    } catch (error) {
      console.error('[AUTH] Error getting token:', error);
      return null;
    }
  },

  async removeToken() {
    try {
      if (isWeb && typeof localStorage !== 'undefined') {
        localStorage.removeItem('authToken');
      }
      await AsyncStorage.removeItem('authToken');
      console.log('[AUTH] Token removed');
    } catch (error) {
      console.error('[AUTH] Error removing token:', error);
    }
  },

  async setUserData(userData) {
    try {
      const data = JSON.stringify(userData);
      if (isWeb && typeof localStorage !== 'undefined') {
        localStorage.setItem('userData', data);
      }
      await AsyncStorage.setItem('userData', data);
    } catch (error) {
      console.error('[AUTH] Error storing user data:', error);
    }
  },

  async getUserData() {
    try {
      let raw;
      if (isWeb && typeof localStorage !== 'undefined') {
        raw = localStorage.getItem('userData');
      }
      if (!raw) {
        raw = await AsyncStorage.getItem('userData');
      }
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error('[AUTH] Error getting user data:', error);
      return null;
    }
  },

  async removeUserData() {
    try {
      if (isWeb && typeof localStorage !== 'undefined') {
        localStorage.removeItem('userData');
      }
      await AsyncStorage.removeItem('userData');
    } catch (error) {
      console.error('[AUTH] Error removing user data:', error);
    }
  },
};

// Request interceptor to add auth token + logging
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await tokenStorage.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('[AUTH] No token found for request:', config.url);
      }
    } catch (error) {
      console.error('[AUTH] Error in request interceptor:', error);
    }
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, config.headers.Authorization ? '(auth:yes)' : '(auth:NO)');
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling + logging
// Retries once on 401 before triggering logout (prevents transient errors from logging out mid-ride)
api.interceptors.response.use(
  (response) => {
    console.log(`[API] ${response.status} ${response.config.url}`, response.data?.success !== undefined ? `success=${response.data.success}` : '');
    return response;
  },
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url || 'unknown';
    const message = error.response?.data?.message || error.message;
    console.error(`[API] ERROR ${status || 'NETWORK'} ${url}: ${message}`);

    if (status === 401 || status === 403) {
      const originalRequest = error.config;

      // Retry once — if this is the first 401 for this request, try again
      if (!originalRequest._authRetried) {
        originalRequest._authRetried = true;
        console.warn(`[AUTH] ${status} on ${url} — retrying once before logout`);
        try {
          return await api(originalRequest);
        } catch (retryError) {
          console.warn(`[AUTH] Retry also failed for ${url} — logging out`);
        }
      }

      // Retry failed or already retried — clear session
      console.warn(`[AUTH] ${status} confirmed on ${url} — clearing session`);
      await tokenStorage.removeToken();
      await tokenStorage.removeUserData();
      if (_onAuthError) {
        _onAuthError();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
